import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, RESORT, WHATSAPP_URL, OFFICIAL_BOOKING_URL } from "@/lib/data";
import SectionTitle from "@/components/SectionTitle";
import RotatingVideo from "@/components/RotatingVideo";
import { HERO_VIDEOS } from "@/lib/videos";
import { Phone, Mail, MapPin, Send, MessageCircle } from "lucide-react";

export default function Contact() {
    const [searchParams] = useSearchParams();
    const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
    const [busy, setBusy] = useState(false);
    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    // Prefill subject + a starter message when the booking page hands off
    // a guest who prefers to arrange payment differently.
    useEffect(() => {
        const subj = searchParams.get("subject");
        if (subj) {
            setForm((f) => ({
                ...f,
                subject: subj,
                message: f.message || "Hi Serenity Cove — I'd like to arrange payment for the reservation above by a method other than PayPal. Please advise on next steps.",
            }));
        }
    }, [searchParams]);

    const submit = async (e) => {
        e.preventDefault();
        try {
            setBusy(true);
            const { data } = await api.post("/contact", form);
            toast.success(data.message);
            setForm({ name: "", email: "", subject: "", message: "" });
        } catch {
            toast.error("Could not send. Please try again.");
        } finally { setBusy(false); }
    };

    return (
        <div data-testid="contact-page">
            <RotatingVideo
                videos={HERO_VIDEOS}
                eager
                testId="contact-page-video"
                className="relative w-full h-[92vh] min-h-[640px]"
                overlay={
                    <>
                        <div className="absolute inset-0 bg-gradient-to-t from-navy/75 via-navy/20 to-navy/40" />
                        <div className="absolute inset-0 max-w-7xl mx-auto px-6 lg:px-10 flex flex-col justify-end pb-16 md:pb-24">
                            <div className="text-[11px] uppercase tracking-[0.28em] text-gold mb-4 flex items-center gap-3"><span className="w-10 h-[1px] bg-gold" /> Contact</div>
                            <h1 className="font-display text-5xl md:text-7xl lg:text-8xl text-ivory max-w-3xl leading-[1.02]">Say <span className="font-serif-italic text-gold">hello</span>.</h1>
                            <p className="mt-6 text-ivory/85 max-w-xl leading-relaxed">Our concierge team responds within a few hours. For urgent matters, please call the number below.</p>
                            <div className="mt-8 flex items-center gap-3">
                                <a href="#contact-form" className="btn-gold px-6 py-3 rounded-full">Send a Message</a>
                                <Link to="/booking" className="btn-outline-ivory px-6 py-3 rounded-full">Book Your Stay</Link>
                            </div>
                        </div>
                    </>
                }
            />
            <div id="contact-form" className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-32 grid md:grid-cols-2 gap-12">
                <form onSubmit={submit} className="bg-white rounded-xl border border-navy/10 p-8 space-y-4">
                    <input required data-testid="contact-name-input" value={form.name} onChange={update("name")} placeholder="Your name" className="w-full border border-navy/15 rounded-lg px-4 py-3 text-sm" />
                    <input required type="email" data-testid="contact-email-input" value={form.email} onChange={update("email")} placeholder="Email" className="w-full border border-navy/15 rounded-lg px-4 py-3 text-sm" />
                    <input data-testid="contact-subject-input" value={form.subject} onChange={update("subject")} placeholder="Subject (optional)" className="w-full border border-navy/15 rounded-lg px-4 py-3 text-sm" />
                    <textarea required rows={6} data-testid="contact-message-input" value={form.message} onChange={update("message")} placeholder="How can we help?" className="w-full border border-navy/15 rounded-lg px-4 py-3 text-sm" />
                    <button disabled={busy} data-testid="contact-submit-button" className="btn-gold px-6 py-3 rounded-full text-sm inline-flex items-center gap-2 disabled:opacity-60">
                        <Send size={14} /> Send message
                    </button>
                </form>
                <div className="space-y-6">
                    <div className="bg-navy text-ivory rounded-xl p-8 relative overflow-hidden grain">
                        <div className="text-[11px] uppercase tracking-[0.28em] text-gold">Visit</div>
                        <div className="font-display text-3xl mt-3">Serenity Cove Resorts</div>
                        <p className="text-ivory/70 mt-2">{RESORT.address}</p>
                        <div className="mt-6 space-y-3 text-sm">
                            <a href="tel:+18766347439" className="flex items-center gap-3 hover:text-gold"><Phone size={14} className="text-gold" /> {RESORT.phone}</a>
                            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-link" className="flex items-center gap-3 hover:text-gold"><MessageCircle size={14} className="text-gold" /> Chat on WhatsApp</a>
                            <a href={`mailto:${RESORT.email}`} className="flex items-center gap-3 hover:text-gold"><Mail size={14} className="text-gold" /> {RESORT.email}</a>
                            <div className="flex items-center gap-3"><MapPin size={14} className="text-gold" /> 87A & 87B Mammee Bay Boulevard, Ocho Rios, Jamaica</div>
                        </div>
                    </div>
                    <div className="rounded-xl overflow-hidden aspect-[4/3] border border-navy/10">
                        <iframe title="Map" src={RESORT.mapEmbed} className="w-full h-full" loading="lazy" />
                    </div>
                </div>
            </div>
        </div>
    );
}
